<script setup>
import { ref, watch, onMounted } from 'vue'
import { UUID } from '@/modules/util/uuid'
import BaseField from '@/components/autoform/BaseField'

const props = defineProps({
	modelValue: {
		type: Object,
		default: () => ({}),
	},
	config: {
		type: Object,
		default: () => ({}),
	},
	fetchers: {
		type: Array,
		default: () => [],
	},
	caching: {
		type: Boolean,
		default: false,
	},
})
const emits = defineEmits(['ready', 'submit', 'update'])

const loading = ref(true)
const form = ref(null)
const valid = ref(true)
const formId = new UUID().get()
const allSections = ref(Object.keys(props.config.sections))
const activeSection = ref(allSections.value[0])
const sectionTabs = ref({})
const rules = (section) => ({
	required: (value) => {
		if (value === undefined) {
			activeSection.value = section
			return 'Field required.'
		}

		return true
	}
})

const validate = async () => {
	if (form.value) {
		await form.value.validate()
	}
}

defineExpose({
	validate,
	valid,
	formId,
})

const conditions = {}
const fetched = {}

/**
 * Get a property at a given path in a dictionary.
 * 
 * The path is assumed traversable. If a property in the path does not exist, an error will be thrown.
 * 
 * This is desired; we should set expected paths beforehand with setProp(), so that our data structure is properly initialized for v-binding.
 * 
 * @param {Object} obj The dictionary to traverse
 * @param {String} path The sequence of properties to traverse, represented as a dot-notation string
 * 
 * @returns {Any} The value at the end of the path
 */
const getProp = (obj, path, debug=false) => {
	return path.split('.').reduce((acc, key) => acc[key], obj)
}

/**
 * Set a property at a given path in a dictionary. Some points to consider:
 * 
 * 1) This function will create any missing properties in the path.
 * 2) When val is undefined, any existing value at the path will be unmodified.
 * 
 * (1) and (2) allow the use of setProp to ensure that a given path is traversable without disturbing existing values.
 * 
 * @param {Object} obj The dictionary to traverse
 * @param {String} path The sequence of properties to traverse, represented as a dot-notation string
 * @param {Any} val The value to set at the end of the path
 * 
 * @returns {Any} The value at the end of the path
 */
const setProp = (obj, path, val) => {    
	const parts = path.split('.')
	const last = parts.pop()
	let current = obj

	parts.forEach(part => {
		if (!(current[part] instanceof Object && !Array.isArray(current[part]))) {
			current[part] = {}
		}
        
		current = current[part]
	})

	current[last] = (val === undefined) ? current[last] : val

	if (!loading.value) {
		emits('update')
	}
}

const processOptions = (field, options=undefined) => {
	if (!options) {
		if (field.options.path) {
			options = getProp(props.modelValue, field.options.path)
		} else if (field.options.fetcher) {
			options = fetched[field.options.fetcher]
		}
	}
	
	if (field.options.label && field.options.value) {
		const labels = field.options.label === '$I'
			? Object.keys(options)
			: field.options.label === '$V'
				? Object.values(options)
				: options.map(o => o[field.options.label])

		const values = field.options.value === '$I'
			? Object.keys(options)
			: field.options.value === '$V'
				? Object.values(options)
				: options.map(o => o[field.options.value])
                        
		options = []
                        
		for (let i = 0; i < labels.length; i++) {
			options[i] = { label: labels[i], value: values[i] }
		}
	} else if (field.options.value) {
		options = field.options.value === '$I'
			? Object.keys(options)
			: field.options.value === '$V'
				? Object.values(options)
				: options = options.map(o => o[field.options.value])
	} else {
		options = Object.values(options)
	}

	return options
}

const processForEach = (instructions, data) => {
	let vals

	if (Object.prototype.hasOwnProperty.call(instructions, 'path')) {
		vals = getProp(data, instructions.path)
	} else if (Object.prototype.hasOwnProperty.call(instructions, 'fetcher')) {
		if (!Object.prototype.hasOwnProperty.call(fetched, instructions.fetcher)) {
			throw new Error(`'${instructions.fetcher}' fetcher not found.`)
		}

		vals = fetched[instructions.fetcher]
	} else {
		throw new Error('Unsupported foreach datasource. Please specify a dot-notation property path for the source datatype OR a valid fetcher.')
	}

	const isDict = vals instanceof Object && !Array.isArray(vals)

	if (isDict) {
		vals = Object.entries(vals)
	}

	if (!Array.isArray(vals)) {
		return
	}

	const labelParts = instructions.label.split('.')

	let getLabel

	if (labelParts.length > 1 && labelParts[0] === '$V') {
		getLabel = (val, i) => getProp(val, instructions.label.substr(3))
	} else if (instructions.label === '$I') {
		getLabel = (val, i) => i
	} else {
		getLabel = (val, i) => instructions.label
	}

	const optionParts = instructions.options.path.split('.')

	let getOptions

	if (optionParts.length > 1 && optionParts[0] === '$V') {
		getOptions = (val) => getProp(val, instructions.options.path.substr(3))
	} else {
		throw new Error('Unsupported option path. Please specify a dot-notation property path for the source datatype.')
	}

	const fields = []
	const base = { ...instructions, display: true }

	let field
	vals.forEach((val, i) => {
		if (isDict) {
			i = val[0]
			val = val[1]
		}

		field = {
			...base,
			label: getLabel(val, i),
			value: instructions.value,
		}

		field.options = processOptions(field, getOptions(val))
		fields.push(field)
	})

	return fields
}

const processIf = (field, condition) => {
	field.display = true

	if (!condition) {
		return 
	}

	const parts = condition.split(' ')

	if (parts.length < 3) {
		return
	}

	const path = parts[0]
	const operator = parts[1]
	const value = parts.slice(2).join(' ')

	const getLeft = (val) => {
		return getProp(props.modelValue, val)
	}

	const compareFuncMap = {
		'===': (a, b) => a === b,
		'==': (a, b) => a == b,
		'!==': (a, b) => a !== b,
		'!=': (a, b) => a != b,
		'>': (a, b) => a > b,
		'<': (a, b) => a < b,
		'>=': (a, b) => a >= b,
		'<=': (a, b) => a <= b,
	}

	const getValue = (val) => {
		if (val === 'true') {
			return true
		} else if (val === 'false') {
			return false
		} else if (!isNaN(val)) {
			return Number(val)
		} else if (val[0] === '\'' && val[val.length - 1] === '\'') {
			return val.substr(1, val.length - 2)
		}
		
		return val
	}

	if (!conditions[path]) {
		conditions[path] = []
	}

	const func = {
		field,
		path,
		left: getLeft,
		compare: compareFuncMap[operator],
		right: getValue(value),
		operation: function () {
			this.field.display = this.compare(this.left(this.path), this.right)
			
			if (!this.field.display && !getProp(props.modelValue, this.field.value)) {
				setProp(props.modelValue, this.field.value, this.field.default 
					? this.field.default
					: this.field.seed
						? getProp(props.modelValue, this.field.seed)
						: null)
			}
		}
	}

	func.operation()

	conditions[path].push(func)
}

onMounted(async () => {
	// Fetch any data required for the form
	await Promise.all(props.fetchers.map(f => (async () => {
		fetched[f.name] = await f()
	})()))

	// Initialize our data object
	Object.values(props.config.sections).forEach(s => {
		if (!s.fields) {
			return
		}

		s.fields.forEach(f => {
			setProp(props.modelValue, f.value)
			if (f.seed) {
				setProp(props.modelValue, f.value, getProp(props.modelValue, f.seed))
			}
		})
	})

	// Initialize the form
	Object.entries(props.config.sections).forEach(s => {
		const name = s[0]
		const section = s[1]

		Object.keys(section).forEach(k => {
			switch (k) {
			case 'fields':
				section[k].forEach(f => {
					if (f.options && !Array.isArray(f.options)) {
						f.options = processOptions(f)
					}
					
					processIf(f, f.if)
				})
				break
			case 'foreach':
				section.fields = processForEach(section[k], props.modelValue)
				break
			default:
				break
			}
		})

		if (section.display === 'tabs') {
			let first = true

			sectionTabs.value[name] = section.fields.reduce((acc, f) => {
				const tab = f.tab ? f.tab : f.label

				if (!acc.tabs[tab]) {
					acc.tabs[tab] = {
						title: tab,
						fields: [],
					}
				}

				if (first) {
					acc.active = tab
					first = false
				}

				acc.tabs[tab].fields.push(f)

				return acc
			}, { tabs: {}, active: '' })
		}
	})
	
	loading.value = false

	emits('ready')
})

watch(props.modelValue, () => {
	Object.values(props.config.sections).forEach(s => {
		if (!s.fields) {
			return
		}

		s.fields.forEach(f => {
			if (conditions[f.value]) {
				conditions[f.value].forEach(c => c.operation())
			}
		})
	})
})
</script>

<template>
<v-tabs
	v-model="activeSection"
	bg-color="blue-grey-lighten-5"
	style="position: sticky; top: 64px; width: 100%; z-index: 1000;"
>
	<v-tab
		v-for="s in allSections"
		:key="s"
		:value="s"
	>
		{{ s }}
	</v-tab>
</v-tabs>

<v-form
	v-if="!loading"
	:id="formId"
	ref="form"
	v-model="valid"
	validate-on="lazy"
	@submit.prevent="emits('submit')"
>
	<v-container>
		<v-lazy
			v-for="s in allSections"
			:key="s"
		>
			<template v-if="!config.sections[s].display || config.sections[s].display === 'grid'">
				<v-row v-show="s === activeSection">
					<v-col
						v-for="f in config.sections[s].fields"
						:key="f.value"
						class="align-self-end"
						:cols="12"
						:sm="f.cols || 12"
					>   
						<BaseField
							v-show="f.display"
							:model-value="getProp(props.modelValue, f.value)"
							:field="f"
							@update:model-value="(val) => {
								setProp(props.modelValue, f.value, val)
								if (conditions[f.value]) {
									conditions[f.value].forEach(c => c.operation())
								}
							}"
						/>

						<v-input
							v-if="f.required"
							:rules="[ rules(s).required ]"
							:model-value="getProp(props.modelValue, f.value)"
						>
							{{ f.label }}
						</v-input>
					</v-col>
				</v-row>
			</template>
			<template v-else-if="config.sections[s].display === 'tabs'">
				<div
					v-show="s === activeSection"
					class="w-100"
				>
					<v-tabs v-model="sectionTabs[s].active">
						<v-tab
							v-for="t in Object.keys(sectionTabs[s].tabs)"
							:key="t"
							:value="t"
						>
							{{ t }}
						</v-tab>
					</v-tabs>

					<div
						v-for="f in sectionTabs[s].tabs[sectionTabs[s].active].fields"
						v-show="f.display"
						:key="f.value"
					>
						<BaseField
							:model-value="getProp(props.modelValue, f.value)"
							:field="f"
							class="mt-4"
							@update:model-value="(val) => {
								setProp(props.modelValue, f.value, val)
								if (conditions[f.value]) {
									conditions[f.value].forEach(c => c.operation())
								}
							}"
						/>
						
						<v-input
							v-if="f.required"
							:rules="[ rules(s).required ]"
							:model-value="getProp(props.modelValue, f.value)"
						>
							{{ f.label }}
						</v-input>
					</div>
				</div>
			</template>
		</v-lazy>
	</v-container>
</v-form>
</template>

<style lang="scss" scoped>

</style>@/modules/util/uuid